import { env } from 'constants';
import useConfig from 'hooks/useConfig';
import { merge } from 'lodash-es';
import { useMemo } from 'react';
import { HIDE_SCROLL_BAR } from './mixins';

const { createTheme } = require('@mui/material');

export const { breakpoints, palette } = createTheme({});
const NEW_TYPOGRAPHY = {
  typography: {
    fontFamily: ['"Montserrat"', 'sans-serif'].join(','),
    'title-h1': {
      fontSize: '96px',
      lineHeight: '116.7%',
      letterSpacing: '-0.8px',
      fontWeight: 800,
    },
    'title-h2': {
      fontSize: '60px',
      lineHeight: '120%',
      letterSpacing: '-0.4px',
      fontWeight: 700,
    },
    'title-h3': {
      fontSize: '48px',
      lineHeight: '48px',
      letterSpacing: '0px',
      fontWeight: 700,
    },
    'title-h4': {
      fontSize: '36px',
      lineHeight: '40px',
      letterSpacing: '0.4px',
      fontWeight: 700,
    },
    'title-h5': {
      fontSize: '24px',
      lineHeight: '32px',
      letterSpacing: '0px',
      fontWeight: 700,
    },
    'title-h6': {
      fontSize: '20px',
      lineHeight: '32px',
      letterSpacing: '0.4px',
      fontWeight: 700,
    },
    'title-lg': {
      fontSize: '18px',
      lineHeight: '24px',
      letterSpacing: '0px',
      fontWeight: 700,
    },
    'title-md': {
      fontSize: '16px',
      lineHeight: '20px',
      letterSpacing: '0px',
      fontWeight: 700,
    },
    'title-sm': {
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0px',
      fontWeight: 700,
    },
    'title-xs': {
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '0px',
      fontWeight: 700,
    },
    'label-lg': {
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0px',
      fontWeight: 600,
    },
    'label-md': {
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0px',
      fontWeight: 600,
    },
    'label-sm': {
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '0px',
      fontWeight: 600,
    },
    'body-lg': {
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0px',
      fontWeight: 500,
    },
    'body-md': {
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0px',
      fontWeight: 500,
    },
    'body-overline': {
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '0px',
      fontWeight: 500,
    },
    'body-caption': {
      fontSize: '12px',
      lineHeight: '16px',
      letterSpacing: '0px',
      fontWeight: 500,
    },
  },
  MuiTypography: {
    defaultProps: {
      variantMapping: {
        'title-h1': 'h1',
        'title-h2': 'h2',
        'title-h3': 'h3',
        'title-h4': 'h4',
        'title-h5': 'h5',
        'title-h6': 'h6',
        'title-lg': 'p',
        'title-md': 'p',
        'title-sm': 'p',
        'title-xs': 'p',
        'label-lg': 'p',
        'label-md': 'p',
        'label-sm': 'p',
        'body-lg': 'p',
        'body-md': 'p',
        'body-overline': 'span',
        'body-caption': 'span',
      },
    },
  },
};

export const rawTheme = {
  palette: {
    ...(env.IS_CATERING ? {
      text: {
        primary: '#212121',
      },
    } : {}),
    text: {
      primary: '#212121',
      secondary: '#757575',
      disabled: '#BDBDBD',
      contrast: '#E0E0E0',
      white: '#FFFFFF',
    },
    background: {
      default: '#FFFFFF',
      elevation1: '#F5F5F5',
      elevation2: '#EEEEEE',
      elevation3: '#E0E0E0',
      20: '#FFFFFF33',
      40: '#FFFFFF66',
      60: '#FFFFFF99',
      80: '#FFFFFFCC',
      error: '#D32F2F1F',
      success: '#388E3C1F',
      info: '#0288D11F',
      warning: '#F57C001F',
    },
    border: {
      default: '#E0E0E0',
      '100%': '#212121',
    },
    action: {
      active: '#0000008F',
      hover: '#0000000A',
      selected: '#00000014',
      focus: '#0000001F',
      disabled: '#00000061',
      disabledbackground: '#0000001F',
    },
    warning: {
      main: '#F57C00',
      dark: '#EF6C00',
      darker: '#E65100',
      light: '#FF9800',
      lighter: '#FFA726',
      contrast: '#FFFFFF',
    },
    info: {
      main: '#0288D1',
      dark: '#0277BD',
      darker: '#01579B',
      light: '#03A9F4',
      lighter: '#29B6F6',
      surface: '#e0f6fe',
      contrast: '#FFFFFF',
    },
    error: {
      main: '#D32F2F',
      dark: '#C62828',
      darker: '#B71C1C',
      light: '#EF5350',
      lighter: '#E57373',
      surface: '#FECDD2',
      contrast: '#FFFFFF',
    },
    success: {
      main: '#388E3C',
      dark: '#2E7D32',
      darker: '#1B5E20',
      light: '#4CAF50',
      lighter: '#66BB6A',
      surface: '#E8F5E9',
      contrast: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Open Sans","Roboto", "Helvetica", "Arial", sans-serif',
    // TODO: remove legacy variant after all pages have been migrated to new typography system
    h1: {
      fontWeight: 'bold',
      fontSize: '3rem',
    },
    h2: {
      fontWeight: 'bold',
      fontSize: '2.5rem',
    },
    h3: {
      fontWeight: 'bold',
      fontSize: '2rem',
    },
    h4: {
      fontWeight: 'bold',
      fontSize: '1.5rem',
    },
    h5: {
      fontWeight: 'bold',
      fontSize: '1.25rem',
      lineHeight: '20px',
    },
    h6: {
      fontWeight: 'bold',
      fontSize: '1rem',
    },
    subtitle1: {
      fontWeight: '500',
      fontSize: '1rem',
    },
    body2: {
      fontWeight: '500',
      fontSize: '0.875rem',
      lineHeight: '20px',
    },
    ...NEW_TYPOGRAPHY.typography,

  },
  spacing: 8,
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography['title-lg'],
          padding: 8,
        }),
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 16,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 16,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: HIDE_SCROLL_BAR,
        paper: {
          margin: '8px 0',
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 12,
          ...theme.typography['label-md'],
          ...theme.components.MuiButton.variants.find((v) => v.props.variant === 'secondary').style,
          '&.Mui-selected': {
            ...theme.components.MuiButton.variants.find((v) => v.props.variant === 'muted').style,
          },
        }),
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'none',
          boxShadow: 'none',
          borderRadius: 12,
          border: '2px solid transparent',
          padding: '12px 16px',
          '&:hover': {
            boxShadow: 'none',
          },
          ...theme.typography['label-md'],
        }),
        sizeLarge: ({ theme }) => ({
          padding: 16,
          ...theme.typography['label-lg'],
        }),
        sizeMedium: {
          padding: '12px 16px',
        },
        sizeSmall: {
          padding: '8px 12px',
        },
        startIcon: {
          '&>*': {
            fontSize: '24px !important',
          },
        },

      },
      variants: [
        {
          props: { variant: 'primary' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
            },
            '&:disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              color: theme.palette.text.disabled,
            },
          }),
        },
        {
          props: { variant: 'secondary' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.grey['100'],
            color: theme.palette.text.primary,
            '&:hover': {
              backgroundColor: theme.palette.grey['200'],
            },
            '&:focus': {
              backgroundColor: theme.palette.grey['100'],
              borderColor: 'rgba(0, 0, 0, 0.3)',
            },
            '&:disabled': {
              backgroundColor: theme.palette.text.disabled,
              color: theme.palette.text.contrast,
            },
          }),
        },
        {
          props: { variant: 'muted' },
          style: ({ theme: { palette } }) => ({
            backgroundColor: palette.text.primary,
            color: palette.text.contrast,
            '&:hover': {
              backgroundColor: `background: linear-gradient(0deg, ${palette.text.primary}, ${palette.text.primary}),
linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1));`,
            },
            '&:disabled': {
              backgroundColor: palette.text.disabled,
            },
            '&:focus': {
              backgroundColor: `background: linear-gradient(0deg, ${palette.text.primary}, ${palette.text.primary}),
linear-gradient(0deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1));`,
              borderColor: '#0000004D',
            },
            '&:active': {
              backgroundColor: `background: linear-gradient(0deg, ${palette.text.primary}, ${palette.text.primary}),
linear-gradient(0deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.2));`,
            },
          }),
        },

        {
          props: { variant: 'link' },
          style: ({ theme }) => ({
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            '&:disabled': {
              color: theme.palette.text.disabled,
            },
          }),
        },
      ],
      defaultProps: {
        disableRipple: true,
        variant: 'secondary',
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          ...(!env.IS_CATERING ? {
            borderRadius: 12,
            backgroundColor: '#F3F4F4',
            color: palette.text.primary,
          } : {}),
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(1px)',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography['body-md'],
          borderRadius: 8,
        }),
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography['title-md'],
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: ({ theme }) => ({
          '&::placeholder': {
            ...theme.typography['label-md'],
          },
          ...theme.typography['label-md'],
        }),
        inputSizeSmall: {
          padding: '12px !important',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          '& .MuiIconButton-root': {
            backgroundColor: 'unset',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#212121',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...theme.typography['label-md'],
          color: '#757575',
          '&.Mui-focused': {
            color: '#212121',
          },
        }),
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: () => ({
          color: '#757575',
        }),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        anchorOriginTopCenter: {
          top: 'max(env(safe-area-inset-top), 8px)',
          [breakpoints.down('sm')]: {
            left: 60,
            right: 60,
            '& .MuiAlert-root': {
              borderRadius: 24,
              justifyContent: 'center',
            },
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          '&:last-child': {
            paddingBottom: 8,
          },
        },
      },
    },
    MuiTypography: {
      ...NEW_TYPOGRAPHY.MuiTypography,
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'capitalize',
          fontSize: 14,
          '&.Mui-selected': {
            fontWeight: 700,
          },
        },
      },
    },
  },
};

export default function useNewTheme(theme) {
  const palette = useConfig('general.theme.palette');
  const primaryColor = useMemo(() => {
    if (!palette) {
      return { main: env.PRIMARY_COLOR || '#212121' };
    }
    if (typeof palette.primary === 'object') {
      return palette.primary;
    }
    return {
      main: palette.primary || palette.primary0,
    };
  }, [palette]);

  return useMemo(() => {
    if (theme) {
      return createTheme(merge({}, {
        ...rawTheme,
        palette: {
          ...rawTheme.palette,
          primary: primaryColor,
        },
      }, theme));
    }
    return createTheme({
      ...rawTheme,
      palette: {
        ...rawTheme.palette,
        primary: primaryColor,
      },
    });
  }, [theme, primaryColor]);
}
